"use client";

import { Award, BookOpen, GraduationCap, Microscope, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Excellence() {
  const heading = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const credentials = [{
    icon: ShieldCheck,
    name: "Board-Certified Expertise",
    type: "DNP, RN, FNP-C",
    status: "Advanced Clinical Practice"
  }, {
    icon: Award,
    name: "Refined Facial Balancing",
    type: "Symmetry & Proportion",
    status: "Natural Results"
  }, {
    icon: GraduationCap,
    name: "Preventive Aesthetics",
    type: "Early Intervention",
    status: "Wrinkle Prevention"
  }, {
    icon: Microscope,
    name: "Medical-Grade Safety",
    type: "Premium Products",
    status: "Highest Standards"
  }, {
    icon: Stethoscope,
    name: "Concierge Care",
    type: "Privacy & Comfort",
    status: "Boutique Experience"
  }, {
    icon: BookOpen,
    name: "Evidence-Based Practice",
    type: "Clinical Science",
    status: "Patient Safety First"
  }];
  return <section data-section-id="14655" id="clinical-excellence" className="bg-background py-28 md:py-36 text-foreground">
      <div className="mx-auto max-w-[1240px] px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={heading.ref} className="lg:col-span-6 max-w-[28rem]">
            <p className={`font-default text-[0.8125rem] font-bold tracking-[0.2em] uppercase text-primary transition-all duration-[800ms] ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "0ms"
          }}>
              Clinical Standards
            </p>

            <h2 className={`font-default font-medium text-foreground mt-5 transition-all duration-[800ms] ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.25rem, 4.2vw, 3.5rem)",
            letterSpacing: "-0.015em",
            lineHeight: "1.1",
            transitionDelay: "80ms"
          }}>
              Refined beauty through{" "}
              <span className="relative inline-block">
                <em className="font-serif italic font-light">precision</em>
                <span className="absolute left-1 right-1 h-[2px] rounded-lg" style={{
                backgroundColor: "var(--primary)",
                bottom: "-4px",
                clipPath: heading.isVisible ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
                transition: "clip-path 700ms ease-out 200ms"
              }} />
              </span>{" "}
              and care.
            </h2>

            <p className={`font-default text-foreground/80 mt-8 transition-all duration-[800ms] ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1rem, 1.2vw, 1.0625rem)",
            lineHeight: "1.7",
            transitionDelay: "160ms"
          }}>At&nbsp; Ideal Image Aesthetics and Wellness, LLC we combine advanced clinical expertise with a concierge approach. From facial balancing to preventive filler treatments, every procedure is designed to restore symmetry and enhance your natural features with effortless, long-lasting results.</p>

            <div className={`mt-10 transition-all duration-[800ms] ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "240ms"
          }}>
              <Link to="/PhilosophyPage" className="font-default font-medium text-foreground inline-flex items-center gap-2 pb-1 border-b border-primary hover:border-b-2 transition-all duration-[250ms]" style={{
              borderColor: "var(--primary)"
            }}>
                Learn about our philosophy
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {}
          <div ref={grid.ref} className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {credentials.map((credential, i) => {
              const Icon = credential.icon;
              const column = i % 3;
              return <div data-index={i} key={i} className="bg-card p-6 flex flex-col items-center text-center transition-all duration-[800ms] ease-out shadow-sm" style={{
                borderRadius: "1rem",
                opacity: grid.isVisible ? 1 : 0,
                transform: grid.isVisible ? "translateY(0)" : "translateY(16px)",
                transitionDelay: `${column * 60}ms`
              }}>
                    <div className="flex items-center justify-center mb-5 bg-primary/5 rounded-lg" style={{
                  height: "56px",
                  width: "56px"
                }}>
                      <Icon strokeWidth={1.5} className="text-primary" style={{
                    width: "28px",
                    height: "28px"
                  }} aria-hidden="true" />
                    </div>
                    <p className="font-default font-semibold text-foreground" style={{
                  fontSize: "0.8125rem",
                  lineHeight: "1.4"
                }}>
                      {credential.name}
                    </p>
                    <p className="font-default text-foreground/70 mt-2" style={{
                  fontSize: "0.8125rem",
                  lineHeight: "1.4"
                }}>
                      {credential.type}
                    </p>
                    <p className="font-default text-foreground/60 mt-1" style={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.01em"
                }}>
                      {credential.status}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}